import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {AccessibleGridCell, GridCell, GridType, isAccessibleGridCell} from 'constants/SampleConstants';
import {FillDirection} from 'lookup-config/LookupConfigModalConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {flattenAndFilterCells, getSortedSelectedCells} from 'samples/grids/GridUtil';
import ScanToMoveGridTab from 'samples/grids/ScanToMoveGridTab';
import {setFillDirection, setIsScanMode, setSelectedCells} from 'samples/GridUIReduxActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import APIUtil from 'util/APIUtil';
import {Deferred} from 'util/makeDeferred';

import GridCellTable from './GridCellTable';
import GridCellToolbar from './GridCellToolbar';
import {GridTabChildProps} from './GridContainer';
import useGridContentLots from './hooks/useGridContentLots';

export interface GridContentsProps extends GridTabChildProps {}

// Stable fallback so the selector below doesn't return a new array reference on every call.
const EMPTY_SELECTED_CELLS: Array<AccessibleGridCell> = [];

/**
 * Contents tab for a grid entity which displays information on each cell of the grid
 * and a toolbar to perform actions on selections of cells.
 */
export default function GridContents({grid}: GridContentsProps): JSX.Element {
  useGridContentLots(grid);
  const dispatch = useDispatch<BenchlingDispatch>();
  const selectedCells = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[grid.id]?.selectedCells ?? EMPTY_SELECTED_CELLS
  );

  function handleSelectCells(selectedCells: Array<AccessibleGridCell>): void {
    dispatch(setSelectedCells(grid.id, selectedCells));
  }

  const isScanMode = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[grid.id]?.isScanMode ?? false
  );

  function handleSetIsScanMode(isScanMode: boolean): void {
    dispatch(setIsScanMode(grid.id, isScanMode));
  }

  const allCells: Array<GridCell> = flattenAndFilterCells(grid.cells);
  const accessibleCells: Array<AccessibleGridCell> = allCells.filter((cell): cell is AccessibleGridCell =>
    isAccessibleGridCell(cell)
  );
  const gridType = APIUtil.apiIdToType(grid.id) as unknown as GridType;
  const sortedSelectedCells = getSortedSelectedCells(grid, selectedCells);

  const fillDirection = useSelector(
    (state: BenchlingState) => state.ui.samples.gridUI[grid.id]?.fillDirection ?? FillDirection.ACROSS_ROWS
  );
  const [cancelPromiseKey, setCancelPromiseKey] = useState<Deferred<string>>();
  function handleSubmitScanToFillModal(fillDirection: FillDirection): void {
    dispatch(setFillDirection(grid.id, fillDirection));
    handleSetIsScanMode(true);
  }

  function exitScanMode(): void {
    cancelPromiseKey?.resolve('ABORTED');
    handleSetIsScanMode(false);
  }

  return (
    <div className={css(styles.tab)} {...getTestProps({component: 'GridContents', element: 'root'})}>
      {isScanMode ? (
        <ScanToMoveGridTab
          fillDirection={fillDirection}
          setCancelPromiseKey={setCancelPromiseKey}
          exitScanMode={exitScanMode}
          grid={grid}
          gridType={gridType}
        />
      ) : (
        <>
          <div className={css(styles.toolbar)}>
            <GridCellToolbar
              cells={accessibleCells}
              grid={grid}
              isWritable={grid.permissions.writable}
              selectedCells={sortedSelectedCells}
              onSubmitScanToFillModal={handleSubmitScanToFillModal}
            />
          </div>
          <div className={css(styles.tableContainer)}>
            <GridCellTable
              cells={allCells}
              grid={grid}
              gridType={gridType}
              onSelectCells={handleSelectCells}
              selectedCells={sortedSelectedCells}
            />
          </div>
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  tab: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '16px 16px 16px 0',
  },
  tableContainer: {
    height: '100%',
    flex: 1,
  },
});
